import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from "../../config/firebase";
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import Movie from './movie';
import TvShow from './tvShow';
import { Hearts } from 'react-loader-spinner';

const Favorites = () => {
    const [favorites, setFavorites] = useState({ movies: [], shows: [] });
    const [partnerFavorites, setPartnerFavorites] = useState({ movies: [], shows: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [connectedUserId, setConnectedUserId] = useState(null);
    const [partnerName, setPartnerName] = useState("");
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    const isInCurrentUserFavorites = (item) => {
        return favorites.movies.some(fav => fav.id === item.id) ||
            favorites.shows.some(fav => fav.id === item.id);
    };

    const isInPartnerFavorites = (item) => {
        return partnerFavorites.movies.some(fav => fav.id === item.id) ||
            partnerFavorites.shows.some(fav => fav.id === item.id);
    };

    // Setup real-time listeners for favorites
    useEffect(() => {
        if (!currentUser) return;

        const unsubscribers = [];
        setIsLoading(true);

        // Set up listener for current user's favorites
        const userFavoritesListener = onSnapshot(
            doc(db, "favoriteContent", currentUser.uid),
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    const favoritesObj = data.favorites || {};
                    
                    // Parse favorites into movies and shows
                    const movies = [];
                    const shows = [];
                    
                    Object.values(favoritesObj).forEach(item => {
                        if (item.type === 'movie') {
                            movies.push(item);
                        } else if (item.type === 'tv') {
                            shows.push(item);
                        }
                    });
                    
                    setFavorites({ movies, shows });
                } else {
                    setFavorites({ movies: [], shows: [] });
                }
                setIsLoading(false);
            },
            (error) => {
                console.error("Error listening to user favorites:", error);
                setIsLoading(false);
            }
        );
        unsubscribers.push(userFavoritesListener);

        // Set up relationship and partner favorites listener
        const setupPartnerListener = async () => {
            try {
                const userInfoDoc = await getDoc(doc(db, "UserInformation", currentUser.uid));
                
                if (userInfoDoc.exists()) {
                    const userData = userInfoDoc.data();
                    
                    if (userData.inRelation && userData.inRelationWith) {
                        setConnectedUserId(userData.inRelationWith);
                        
                        // Get partner's name
                        const partnerInfoDoc = await getDoc(doc(db, "UserInformation", userData.inRelationWith));
                        if (partnerInfoDoc.exists()) {
                            setPartnerName(partnerInfoDoc.data().name || "");
                        }

                        // Set up listener for partner's favorites
                        const partnerFavoritesListener = onSnapshot(
                            doc(db, "favoriteContent", userData.inRelationWith),
                            (docSnapshot) => {
                                if (docSnapshot.exists()) {
                                    const data = docSnapshot.data();
                                    const favoritesObj = data.favorites || {};
                                    
                                    // Parse favorites into movies and shows
                                    const movies = [];
                                    const shows = [];
                                    
                                    Object.values(favoritesObj).forEach(item => {
                                        if (item.type === 'movie') {
                                            movies.push(item);
                                        } else if (item.type === 'tv') {
                                            shows.push(item);
                                        }
                                    });
                                    
                                    setPartnerFavorites({ movies, shows });
                                } else {
                                    setPartnerFavorites({ movies: [], shows: [] });
                                }
                            },
                            (error) => {
                                console.error("Error listening to partner favorites:", error);
                                setPartnerFavorites({ movies: [], shows: [] });
                            }
                        );
                        unsubscribers.push(partnerFavoritesListener);
                    } else {
                        setConnectedUserId(null);
                        setPartnerName("");
                        setPartnerFavorites({ movies: [], shows: [] });
                    }
                } else {
                    setConnectedUserId(null);
                    setPartnerName("");
                    setPartnerFavorites({ movies: [], shows: [] });
                }
            } catch (error) {
                console.error('Error setting up partner listener:', error);
            }
        };

        setupPartnerListener();

        // Cleanup function
        return () => {
            unsubscribers.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
        };
    }, [currentUser]);


    // Combine both users' favorites
    const combinedFavorites = {
        movies: [...new Map([
            ...favorites.movies.map(movie => [movie.id, movie]),
            ...partnerFavorites.movies.map(movie => [movie.id, movie])
        ]).values()],
        shows: [...new Map([
            ...favorites.shows.map(show => [show.id, show]),
            ...partnerFavorites.shows.map(show => [show.id, show])
        ]).values()]
    };

    return (
        <div className="favorites-container">
            {isLoading ? (
                <div className="loading-indicator">
                    <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                    <p>Loading favorites...</p>
                </div>
            ) : (
                <TabView className="tab-view-container">
                    <TabPanel header="Movies" className="favorite-movies-tab">
                        <div className="favorites-content">
                            {combinedFavorites.movies.length > 0 ? (
                                <div className="favorites-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 3fr))',
                                    gap: '1rem'
                                }}>
                                    {combinedFavorites.movies.map((movie, index) => (
                                        <Card key={`${movie.id}-${index}`} className="favorite-card">
                                            <Movie
                                                id={movie.id}
                                                title={movie.title}
                                                year={movie.year}
                                                image={movie.image}
                                                rating={movie.rating}
                                                type="movie"
                                                isFavorite={isInCurrentUserFavorites(movie)}
                                                isPartnerFavorite={isInPartnerFavorites(movie)}
                                                currentUserId={currentUser.uid}
                                                partnerUserId={connectedUserId}
                                                savedBy={movie.savedBy}
                                            />
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-content">
                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                                    <p>No favorite movies yet.</p>
                                </div>
                            )}
                        </div>
                    </TabPanel>

                    <TabPanel header="TV Shows" className="favorite-shows-tab">
                        <div className="favorites-content">
                            {combinedFavorites.shows.length > 0 ? (
                                <div className="favorites-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: '1rem'
                                }}>
                                    {combinedFavorites.shows.map((show, index) => (
                                        <Card key={`${show.id}-${index}`} className="favorite-card">
                                            <TvShow
                                                id={show.id}
                                                title={show.title}
                                                year={show.year}
                                                image={show.image}
                                                rating={show.rating}
                                                type="tv"
                                                isFavorite={isInCurrentUserFavorites(show)}
                                                isPartnerFavorite={isInPartnerFavorites(show)}
                                                currentUserId={currentUser.uid}
                                                partnerUserId={connectedUserId}
                                                savedBy={show.savedBy}
                                            />
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-content">
                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                                    <p>No favorite TV shows yet.</p>
                                </div>
                            )}
                        </div>
                    </TabPanel>
                </TabView>
            )}
        </div>
    );
};

export default Favorites;
